import { CoreImageView, CoreTextView } from '@core/widgets';
import { Loading } from '@libs/core';
import { AssetsSvgMap, TViewCommonOption } from '@libs/types';
import clsx from 'clsx';
import React, { Suspense, useDeferredValue, useMemo, useRef, useState } from 'react';
import { TConsumerItem } from '../../../types/data';
import { TextStyle } from '../../../types/styles';
import { getHref } from '../../Image/imageUtils';
import InputSearch from '../InputSearch';
import ListItems, { TListItemProps } from '../ListItems';
import styles from '../styles/styles.module.css';
import { filterBySearch } from '../utils/search';
import { useSearchHighlight } from '../utils/useSearchHighlight';

type TListSearchPopupProps = {
    renderItem: React.FC<TListItemProps>;
    items: TConsumerItem[];
    index: number;
    header: string;
    noFoundText?: string;
    searchPlaceholder?: string;
} & Pick<TViewCommonOption, 'styleName' | 'parentStyleNames'>;

const ListSearchPopup = ({
    items,
    renderItem,
    index,
    header,
    searchPlaceholder,
    noFoundText = '',
    styleName = 'listSearchTitle',
    parentStyleNames,
}: TListSearchPopupProps) => {
    const [isSearchMode, setIsSearchMode] = useState(false);
    const [query, setQuery] = useState('');
    const deferredQuery = useDeferredValue(query);
    const listRef = useRef<HTMLDivElement>(null);
    const hasItems = items && Array.isArray(items) && items.length > 0;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value);

    const memorizedItems = useMemo(() => (deferredQuery ? filterBySearch(deferredQuery, items) : []), [items, deferredQuery]);

    useSearchHighlight(listRef, deferredQuery, memorizedItems);

    const searchBlock = !isSearchMode ? (
        <>
            <CoreTextView
                text={header}
                styleName={styleName}
                parentStyleNames={parentStyleNames}
                className={styles.searchPopupHeader}
                styleProps={{
                    ...TextStyle.main,
                    main: 'var(--black)',
                    textAlign: 'center' as const,
                }}
            />
            <InputSearch
                searchPlaceholder={searchPlaceholder}
                uid="inputSearch"
                asButton={true}
                handleClick={() => setIsSearchMode(true)}
                marginBottom={24}
                marginLeft={24}
                marginRight={24}
            />
        </>
    ) : (
        <InputSearch
            searchPlaceholder={searchPlaceholder}
            uid="inputSearch"
            asButton={false}
            isSearchInput={true}
            value={query}
            autoFocus={true}
            handleChange={handleChange}
            marginBottom={16}
            handleCloseClick={() => {
                setIsSearchMode(false);
                setQuery('');
            }}
            backIcon={AssetsSvgMap.ic_chevron_down}
            handleClickRightComponent={() => setQuery('')}
        />
    );
    return (
        <Suspense fallback={<Loading />}>
            {searchBlock}
            {memorizedItems.length > 0 && (
                <div ref={listRef} data-test="search-results" className={styles.searchResults}>
                    <ListItems items={memorizedItems} renderItem={renderItem} currentIndex={index} />
                </div>
            )}
            {!deferredQuery && memorizedItems.length === 0 && hasItems && <ListItems items={items} renderItem={renderItem} currentIndex={index} />}
            {deferredQuery && memorizedItems.length === 0 && (
                <div className={clsx(styles.emptyContainer, styles.leftCenterAligned)}>
                    <CoreImageView
                        src={getHref(AssetsSvgMap.ic_ghost)}
                        styleProps={{
                            iconColor: `var(--grey_72_lc)`,
                        }}
                    />
                    <CoreTextView
                        text={`${noFoundText} "${query}"`}
                        styleProps={{ ...TextStyle.secondary, main: 'var(--grey_72_lc)', margin: { top: 16 }, textAlign: 'center' }}
                    />
                </div>
            )}
        </Suspense>
    );
};
export default ListSearchPopup;
